import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../basic';
import { Spinner } from '../../../../common';
import {
  useGetDataItemsQuery,
  useGetSectionQuery,
  useGetYellowBookQuery,
  useSaveSectionMutation,
} from '../../../../../redux/services/yellow_books';
import SectionForm from './SectionForm.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { PlusOutlined } from '@ant-design/icons';

const SectionsEdit = ({ mode }) => {
  const { yellowBookId, sectionId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { saveSuccess: showSaveSuccess } = useToastMessages();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isCopy = mode === 'copy';
  const isChild = mode === 'child';

  const title =
    (isCreate && t('Pridanie sekcie do žltej knižky')) ||
    t('Úprava sekcie v žltej knižke');

  useEffect(() => {
    document.title = title + ' - FRI CMS';
  }, []); // Runs on mount

  const { data: yellowBookSection, isLoading } = useGetSectionQuery(
    { yellowBookId, sectionId },
    { skip: !isEdit },
  );

  const { data: dataItems } = useGetDataItemsQuery();

  const [saveSection, { isSuccess: isSaveSuccess }] = useSaveSectionMutation();

  const { refetch } = useGetYellowBookQuery(yellowBookId);

  useEffect(() => {
    if (isSaveSuccess) {
      showSaveSuccess();
      navigate(routes.cmsYellowBooksSections(yellowBookId));
      refetch();
    }
  }, [isSaveSuccess]);

  if (isEdit && isLoading) return <Spinner />;

  const copyOrChildData = { item_type: 'text' };

  const initials =
    isChild || isCopy
      ? copyOrChildData
      : (yellowBookSection ?? {
          item_type: 'text',
        });

  const mainActions = [
    isEdit && {
      title: t('Vytvoriť podsekciu'),
      icon: <PlusOutlined />,
      permission: 'yellow-books.store',
      onClick: () =>
        navigate(routes.cmsYellowBookSectionsChild(yellowBookId, sectionId)),
    },
    isEdit && {
      title: t('Kopírovať sekciu'),
      icon: <PlusOutlined />,
      permission: 'yellow-books.store',
      onClick: () =>
        navigate(routes.cmsYellowBookSectionsCopy(yellowBookId, sectionId)),
    },
  ].filter(Boolean);

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={routes.cmsYellowBooksSections(yellowBookId)}
        backUrlTooltip={t('Žltá knižka')}
        permissionsBasic="yellow-books"
        mainActions={mainActions}
      />
      <div style={{ margin: '10px' }}>
        <SectionForm
          initials={initials}
          isResetForm={isChild ? Date.now() : null}
          dataItems={dataItems}
          onSubmit={(values) => {
            const submitPayload = {
              yellowBookId,
              body: {
                ...values,
                ...(isChild ? { parent_id: sectionId } : {}),
                ...(isCopy
                  ? { parent_id_copy: sectionId, parent_id: null }
                  : {}),
              },
            };

            if (isEdit && !isCopy && !isChild) {
              submitPayload.sectionId = sectionId;
            }

            saveSection(submitPayload);
          }}
        />
      </div>
    </>
  );
};

export default SectionsEdit;
